/**
 * Factory helpers to create tactic nodes.
 * Keeps statement construction consistent across the editor.
 */

// Token entries are either a plain number or { value, group }
export const getTokenValue = (token) =>
  typeof token === "object" ? token.value : token;

// Reverse lookup: numeric value → display label
export const buildValueToLabel = (tokenMap) =>
  Object.fromEntries(
    Object.entries(tokenMap).map(([label, token]) => [getTokenValue(token), label])
  );

// Group tokens by their group field → { groupName: [{ label, value }] }
export const groupTokens = (tokenMap) => {
  const groups = {};
  for (const [label, token] of Object.entries(tokenMap)) {
    const group = typeof token === "object" ? (token.group ?? "Other") : "Other";
    const value = getTokenValue(token);
    if (!groups[group]) groups[group] = [];
    groups[group].push({ label, value });
  }
  return groups;
};

// arg (optional) = the argument of a parametrized action, e.g. a zone value
// (already the final stream int, 2000+z). Omitted for plain actions.
// callTarget (optional) = callee tactic id for a CALL_TACTIC action (its OWN
// slot, NOT arg — kept apart so future call params reuse arg/arguments).
export const createAction = (action, arg = null, callTarget = null) =>
  ({ kind: "action", action, ...(arg != null ? { arg } : {}), ...(callTarget != null ? { callTarget } : {}) });

export const createConditionalAction = (condition, action, arg = null, callTarget = null) =>
  ({ kind: "conditional_action", condition, action, ...(arg != null ? { arg } : {}), ...(callTarget != null ? { callTarget } : {}) });

export const createIf = (condition, then_, else_) =>
  ({ kind: "if", condition, then: then_, ...(else_ ? { else: else_ } : {}) });

export const createBlock = (statements = []) =>
  ({ kind: "block", statements });

// arg (optional) = the argument of a parametrized query, e.g. a zone value
// (already the final stream int, 2000+z). Omitted for plain queries.
export const createAtom = (value, arg = null) =>
  ({ kind: "atom", value, ...(arg != null ? { arg } : {}) });

export const createNot = (operand) =>
  ({ kind: "not", operand });

export const createAnd = (left, right) =>
  ({ kind: "and", left, right });

export const createOr = (left, right) =>
  ({ kind: "or", left, right });

// Syntax token integers — must match tokens.hh
const LPAR = 0, RPAR = 1, LBRACKET = 2, RBRACKET = 3, IF = 4, ELSE = 5, AND = 6, OR = 7, NOT = 8;
const QUERY_OFFSET = 100, ACTION_OFFSET = 1000, ARGUMENT_OFFSET = 2000;
// CALL: must match tokens.hh (action CALL_TACTIC = 30; targetOffset). The callee
// tactic id travels as (TARGET_OFFSET + id) in its OWN token kind, kept apart
// from arguments so future call params (argument tokens after it) never collide.
const TARGET_OFFSET = 100000, CALL_TACTIC = ACTION_OFFSET + 30;

// ── Validation ──────────────────────────────────────────────────────────────

/** Walk the tree and return a list of problem keys (empty = valid). */
export function validateStatements(stmts) {
  const errors = [];
  for (const s of stmts) {
    if (s.kind === "if") {
      if (!s.then || s.then.length === 0) errors.push("empty_if");
      else errors.push(...validateStatements(s.then));
      if (s.else?.length) errors.push(...validateStatements(s.else));
      else if (s.else && s.else.length === 0) errors.push("empty_else");
    } else if (s.kind === "conditional_action") {
      if (s.action == null) errors.push("missing_action");
    } else if (s.kind === "block") {
      if (!s.statements?.length) errors.push("empty_block");
      else errors.push(...validateStatements(s.statements));
    }
  }
  return errors;
}

// ── Serializer: editor objects → flat int list ──────────────────────────────

function serializeQuery(q) {
  if (q.kind === "atom") return q.arg != null ? [q.value, q.arg] : [q.value];
  if (q.kind === "not")  return [NOT, ...serializeQuery(q.operand)];
  if (q.kind === "and")  return [...serializeQuery(q.left), AND, ...serializeQuery(q.right)];
  if (q.kind === "or")   return [...serializeQuery(q.left), OR,  ...serializeQuery(q.right)];
  return [];
}

// C++ grammar expects a single statement for then/else branches.
// Wrap arrays in a block when needed, unwrap single-item arrays.
function serializeBranch(stmts) {
  if (stmts.length === 0) return [LBRACKET, RBRACKET];
  if (stmts.length === 1) return serializeStatements(stmts);
  return [LBRACKET, ...serializeStatements(stmts), RBRACKET];
}

export function serializeStatements(stmts) {
  const out = [];
  for (const s of stmts) {
    if (s.kind === "action") {
      out.push(s.action);
      if (s.action === CALL_TACTIC && s.callTarget != null) out.push(TARGET_OFFSET + s.callTarget);
      else if (s.arg != null) out.push(s.arg);
    } else if (s.kind === "conditional_action") {
      out.push(IF, LPAR, ...serializeQuery(s.condition), RPAR, s.action);
      if (s.action === CALL_TACTIC && s.callTarget != null) out.push(TARGET_OFFSET + s.callTarget);
      else if (s.arg != null) out.push(s.arg);
    } else if (s.kind === "if") {
      out.push(IF, LPAR, ...serializeQuery(s.condition), RPAR, ...serializeBranch(s.then ?? []));
      if (s.else?.length) out.push(ELSE, ...serializeBranch(s.else));
    } else if (s.kind === "block") {
      out.push(LBRACKET, ...serializeStatements(s.statements ?? []), RBRACKET);
    }
  }
  return out;
}

// ── Deserializer: flat int list → editor objects ─────────────────────────────

function isSyn(t, s)   { return t < QUERY_OFFSET && t === s; }
function isQuery(t)    { return t >= QUERY_OFFSET && t < ACTION_OFFSET; }
function isAction(t)   { return t >= ACTION_OFFSET && t < ARGUMENT_OFFSET; }
function isArgument(t) { return t >= ARGUMENT_OFFSET && t < TARGET_OFFSET; }
function isCallee(t)   { return t >= TARGET_OFFSET; }

function peek(p)    { return p.tokens[p.pos]; }
function advance(p) { return p.tokens[p.pos++]; }
function expect(p, s) {
  if (!isSyn(peek(p), s)) throw new Error(`Expected syntax ${s} at pos ${p.pos}`);
  advance(p);
}

// nodeId contract with the engine: a node's id is the OFFSET of its FIRST token
// in the flat stream (see Docs/rule-inspector.txt). Both parsers walk the SAME
// stream, so anchoring to the start-token index survives the shape differences
// between the two trees (the editor flattens branch blocks the engine keeps).
// Composite nodes (AND/OR) use their OPERATOR token's offset — a UNIQUE id that
// must NOT collide with the left operand's offset (it did before, so the left
// atom got painted with the composite's value in the inspector). Parens create
// no node on either side: the inner node keeps its own offset.
function withOffset(node, off) {
  node.tokenOffset = off;
  return node;
}

function parseOr(p) {
  let left = parseAnd(p);
  while (p.pos < p.tokens.length && isSyn(peek(p), OR)) {
    const opOff = p.pos; // the OR token
    advance(p);
    left = withOffset(createOr(left, parseAnd(p)), opOff);
  }
  return left;
}

function parseAnd(p) {
  let left = parseNot(p);
  while (p.pos < p.tokens.length && isSyn(peek(p), AND)) {
    const opOff = p.pos; // the AND token
    advance(p);
    // RHS must go through parseNot (like the engine's parseAnd), otherwise a
    // NOT on the right of an AND ("A Y NO B") isn't parsed and the tree/stream
    // diverges from the engine (round-trip threw before this fix).
    left = withOffset(createAnd(left, parseNot(p)), opOff);
  }
  return left;
}

function parseNot(p) {
  if (p.pos < p.tokens.length && isSyn(peek(p), NOT)) {
    const off = p.pos;
    advance(p);
    return withOffset(createNot(parseNot(p)), off);
  }
  return parseQueryPrimary(p);
}

function parseQueryPrimary(p) {
  if (isSyn(peek(p), LPAR)) {
    advance(p);
    const q = parseOr(p);
    expect(p, RPAR);
    return q;
  }
  if (isQuery(peek(p))) {
    const off = p.pos;
    const value = advance(p);
    // a parametrized query is followed by its argument (metadata-free:
    // arguments only ever follow a parametrized query in a valid stream)
    const arg = (p.pos < p.tokens.length && isArgument(peek(p))) ? advance(p) : null;
    return withOffset(createAtom(value, arg), off);
  }
  throw new Error(`Expected query at pos ${p.pos}`);
}

// C++ then/else is a single statement; unwrap blocks to arrays for the editor.
function parseBranch(p) {
  const stmt = parseStmt(p);
  return stmt.kind === "block" ? stmt.statements : [stmt];
}

function parseStmt(p) {
  const off = p.pos; // start-token offset = this statement's nodeId (see withOffset)
  const t = peek(p);
  if (isSyn(t, IF)) {
    advance(p);
    expect(p, LPAR);
    const condition = parseOr(p);
    expect(p, RPAR);
    const then_ = parseBranch(p);
    let else_ = undefined;
    if (p.pos < p.tokens.length && isSyn(peek(p), ELSE)) {
      advance(p);
      else_ = parseBranch(p);
    }
    return withOffset(createIf(condition, then_, else_), off);
  }
  if (isSyn(t, LBRACKET)) {
    advance(p);
    const stmts = [];
    while (p.pos < p.tokens.length && !isSyn(peek(p), RBRACKET)) stmts.push(parseStmt(p));
    expect(p, RBRACKET);
    return withOffset(createBlock(stmts), off);
  }
  if (isSyn(t, LPAR)) {
    advance(p);
    const condition = parseOr(p);
    expect(p, RPAR);
    const action = isAction(peek(p)) ? advance(p) : null;
    if (action === CALL_TACTIC) {
      const target = (p.pos < p.tokens.length && isCallee(peek(p))) ? advance(p) - TARGET_OFFSET : null;
      return withOffset(createConditionalAction(condition, action, null, target), off);
    }
    const arg = (action != null && p.pos < p.tokens.length && isArgument(peek(p))) ? advance(p) : null;
    return withOffset(createConditionalAction(condition, action, arg), off);
  }
  if (isAction(t)) {
    const action = advance(p);
    if (action === CALL_TACTIC) {
      const target = (p.pos < p.tokens.length && isCallee(peek(p))) ? advance(p) - TARGET_OFFSET : null;
      return withOffset(createAction(action, null, target), off);
    }
    const arg = (p.pos < p.tokens.length && isArgument(peek(p))) ? advance(p) : null;
    return withOffset(createAction(action, arg), off);
  }
  throw new Error(`Unexpected token ${t} at pos ${p.pos}`);
}

export function deserializeStatements(tokens) {
  if (!tokens?.length) return [];
  const p = { tokens, pos: 0 };
  const stmts = [];
  while (p.pos < p.tokens.length) stmts.push(parseStmt(p));
  return stmts;
}
